import { DBSchema, IDBPDatabase, openDB } from "idb"

export type ImportMapping = {
  date?: string | string[]
  name?: string | string[]
  description?: string | string[]
  amount?: string
  category?: string
  nameSeparator?: string
  descriptionSeparator?: string
  categoryMappings?: Record<string, number | "new">
}

export type ImportConfig = {
  separator: string
  dateFormat: string
  decimalSeparator: "," | "."
}

export type ImportProgress = {
  id: string
  accountId: number
  totalRows: number
  processedRows: number
  successfulRows: number
  failedRows: number
  errors: Array<{ index: number; error: string; row: any }>
  status: "pending" | "in_progress" | "completed" | "failed" | "paused"
  createdAt: number
  updatedAt: number
}

export type ImportSession = {
  id: string
  accountId: number
  parsedRows: any[]
  mapping: ImportMapping
  config: ImportConfig
  progress: ImportProgress
}

interface ImportStoreDB extends DBSchema {
  imports: {
    key: string
    value: ImportSession
    indexes: { accountId: number; status: string }
  }
}

let dbPromise: Promise<IDBPDatabase<ImportStoreDB>> | null = null

function getDB(): Promise<IDBPDatabase<ImportStoreDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ImportStoreDB>("expense-imports", 1, {
      upgrade(db) {
        const store = db.createObjectStore("imports", {
          keyPath: "id",
        })
        store.createIndex("accountId", "accountId")
        store.createIndex("status", "progress.status")
      },
    })
  }
  return dbPromise
}

/**
 * Save an import session
 */
export async function saveImportSession(session: ImportSession): Promise<void> {
  const db = await getDB()
  await db.put("imports", session)
}

/**
 * Get an import session by ID
 */
export async function getImportSession(id: string): Promise<ImportSession | undefined> {
  const db = await getDB()
  return db.get("imports", id)
}

/**
 * Get all import sessions for an account
 */
export async function getImportSessionsByAccount(accountId: number): Promise<ImportSession[]> {
  const db = await getDB()
  const tx = db.transaction("imports", "readonly")
  const index = tx.store.index("accountId")
  return index.getAll(accountId)
}

/**
 * Get in-progress import sessions for an account
 */
export async function getInProgressImports(accountId: number): Promise<ImportSession[]> {
  const db = await getDB()
  const tx = db.transaction("imports", "readonly")
  const index = tx.store.index("status")
  const all = await index.getAll("in_progress")
  return all.filter((session) => session.accountId === accountId)
}

/**
 * Update import progress
 */
export async function updateImportProgress(
  id: string,
  progress: Partial<ImportProgress>
): Promise<void> {
  const session = await getImportSession(id)
  if (!session) {
    throw new Error(`Import session ${id} not found`)
  }

  session.progress = {
    ...session.progress,
    ...progress,
    updatedAt: Date.now(),
  }

  await saveImportSession(session)
}

/**
 * Delete an import session
 */
export async function deleteImportSession(id: string): Promise<void> {
  const db = await getDB()
  await db.delete("imports", id)
}

/**
 * Delete all completed import sessions older than specified days
 */
export async function cleanupOldImports(daysOld: number = 7): Promise<void> {
  const db = await getDB()
  const tx = db.transaction("imports", "readwrite")
  const store = tx.store
  const cutoff = Date.now() - daysOld * 24 * 60 * 60 * 1000

  const all = await store.getAll()
  for (const session of all) {
    if (
      (session.progress.status === "completed" || session.progress.status === "failed") &&
      session.progress.updatedAt < cutoff
    ) {
      await store.delete(session.id)
    }
  }
}
